import { useEffect } from 'react';
import type React from 'react';
import Header from '../../components/layout/Header/index';
import brazilImage from '../../assets/img/brazilImage.webp';
import swissImage from '../../assets/img/suica.webp';
// Served from /public (not bundled) so index.html can <link rel="preload"> it:
// the LCP image is then discoverable during HTML parse instead of after JS runs.
const BlueAlpsWaves = `${process.env.PUBLIC_URL}/hero-waves-v1.webp`;

// The hero is the LCP element, and it spans the full card width - which ranges
// from a phone's viewport to the 1600px cap. Serving one size meant small
// screens downloaded roughly three times the pixels they could show.
// Keep in sync with the matching <link rel="preload"> in public/index.html.
const HERO_SRCSET = [640, 800, 960, 1024, 1280, 1400, 1536]
  .map((w) => `${process.env.PUBLIC_URL}/hero-waves-v1-${w}.webp ${w}w`)
  .join(', ');

// .hero-card is `width: 100%; max-width: 1600px` inside a 16px-padded container.
const HERO_SIZES = '(min-width: 1632px) 1600px, calc(100vw - 32px)';
import TeamSection from '../../components/ui/Team';
import CustomersSection from '../../components/ui/Customer';
import ServicesTeaser from '../../components/ui/ServicesTeaser';
import CardMark from '../../components/ui/Mark';
import '../../styles/Home.css';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackMatomoEvent } from '../../utils/matomo';
import {
  FaGlobeAmericas,
  FaHandshake,
  FaHandsHelping,
  FaLeaf,
  FaShieldAlt,
} from 'react-icons/fa';
import type { IconBaseProps, IconType } from 'react-icons';

const asIconComponent = (Icon: IconType, name: string) => {
  const Comp: React.FC<IconBaseProps> = (props) => {
    const element = Icon(props);
    return element as React.ReactElement;
  };
  Comp.displayName = name;
  return Comp;
};

const purposeValues = [
  { key: 'equity', Icon: asIconComponent(FaHandsHelping, 'EquityIcon') },
  { key: 'inclusion', Icon: asIconComponent(FaGlobeAmericas, 'InclusionIcon') },
  { key: 'integrity', Icon: asIconComponent(FaShieldAlt, 'IntegrityIcon') },
  {
    key: 'collaboration',
    Icon: asIconComponent(FaHandshake, 'CollaborationIcon'),
  },
  {
    key: 'sustainability',
    Icon: asIconComponent(FaLeaf, 'SustainabilityIcon'),
  },
];

const Home = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.scrollToId) {
      const section = document.getElementById(location.state.scrollToId);
      if (section) {
        setTimeout(() => {
          section.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);
  return (
    <div>
      <Header />
      <div className="hero-container hero-v3">
        <section className="hero-card">
          <div className="hero-bg" aria-hidden>
            <img
              src={BlueAlpsWaves}
              srcSet={HERO_SRCSET}
              sizes={HERO_SIZES}
              alt="BlueAlps Waves Background"
              aria-hidden="true"
              className="hero-bg-img"
              width={1280}
              height={854}
              fetchPriority="high"
              decoding="sync"
              loading="eager"
            />
            <div className="hero-overlay" />
            <div className="hero-tech-scan" />
            <div className="hero-tech-grid" />
            <div className="hero-tech-particles" />
            <div className="hero-tech-glow" />
          </div>

          <main className="hero-content hero-v3-content">
            <div className="hero-left">
              <div className="hero-eyebrow">{t('home.hero.eyebrow')}</div>

              <h1 className="hero-title">
                {t('home.hero.title_line1')}
                <br />
                <span className="hero-title-accent">
                  {t('home.hero.title_line2')}
                </span>
              </h1>

              <p className="hero-subtitle">{t('home.caption')}</p>

              <div className="hero-actions">
                <a
                  className="hero-btn hero-btn-ghost"
                  href="/services"
                  onClick={() =>
                    trackMatomoEvent('CTA', 'click', 'Hero services button')
                  }
                >
                  {t('home.more_info_services')}
                </a>
              </div>
            </div>
          </main>
        </section>

        <div className="scroll-prompt">
          <a
            href="#about"
            onClick={() =>
              trackMatomoEvent('Navigation', 'click', 'Hero about')
            }
          >
            {t('home.more_info')} ↓
          </a>
        </div>
      </div>

      <section className="purpose-section" id="about">
        <div className="purpose-container">
          <div className="purpose-text">
            <h2>
              {t('home.purposetitle_part1')}{' '}
              <span> {t('home.purposetitle_part2')}</span>
            </h2>

            <p>{t('home.purpose')}</p>

            <div
              className="purpose-values"
              aria-label={t('home.purpose_values_label')}
            >
              {purposeValues.map(({ key, Icon }) => (
                <div className="purpose-value" key={key}>
                  <Icon className="purpose-value-icon" aria-hidden="true" />
                  <span className="purpose-value-name">
                    {t(`home.purpose_values.${key}`)}
                  </span>
                </div>
              ))}
            </div>

            <a
              className="btn-teaser "
              href="/about"
              onClick={() =>
                trackMatomoEvent('CTA', 'click', 'Purpose company button')
              }
            >
              {t('home.company_cta')}
            </a>
          </div>
        </div>
      </section>

      {/* <section className="accordion-container">
        <Accordion
          defaultActiveKey="0"
          flush
          data-bs-theme={theme}
          onSelect={(eventKey) =>
            eventKey &&
            trackMatomoEvent('Content', 'accordion_open', `Home ${eventKey}`)
          }
        >
          <Accordion.Item eventKey="1">
            <Accordion.Header>{t('home.vision_title')}</Accordion.Header>
            <Accordion.Body className="acoordion">
              {t('home.vision_text')}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>{t('home.mission_title')}</Accordion.Header>
            <Accordion.Body className="acoordion">
              {t('home.mission_text')}
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="3">
            <Accordion.Header>{t('home.core_values')}</Accordion.Header>
            <Accordion.Body className="acoordion">
              <p> {t('home.core_values_text')} </p>

              <ul>
                <li>
                  <strong>{t('home.core_values_list_Equity')}:</strong>{' '}
                  {t('home.core_values_list_Equity_text')}
                </li>
                <li>
                  <strong>
                    {t('home.core_values_list_InclusionDiversity')}:
                  </strong>{' '}
                  {t('home.core_values_list_InclusionDiversity_text')}
                </li>
                <li>
                  <strong>{t('home.core_values_list_Integrity')}:</strong>{' '}
                  {t('home.core_values_list_Integrity_text')}
                </li>
                <li>
                  <strong>{t('home.core_values_list_Sustainability')}:</strong>{' '}
                  {t('home.core_values_list_Sustainability_text')}
                </li>
                <li>
                  <strong>{t('home.core_values_list_Collaboration')}:</strong>{' '}
                  {t('home.core_values_list_Collaboration_text')}
                </li>
              </ul>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </section> */}
      <section className="brand">
        <h2>
          {t('home.two_nations1')} <span>{t('home.two_nations2')}</span>
        </h2>

        <div className="banner-container">
          <div className="banner-content">
            <div className="banner-location">
              <img
                src={swissImage}
                alt="Paisagem da Suíça"
                className="banner-image swiss-image"
                fetchPriority="low"
                decoding="async"
                loading="lazy"
              />

              <span className="location-text swiss">
                {t('home.switzerland')}
              </span>
            </div>
            <div className="banner-location">
              <span className="location-text ">&</span>
            </div>
            <div className="banner-location">
              <img
                src={brazilImage}
                alt="Paisagem do Brasil"
                className="banner-image brazil-image"
                fetchPriority="low"
                decoding="async"
                loading="lazy"
              />
              <span className="location-text brazil">{t('home.brazil')}</span>
            </div>
          </div>
        </div>
      </section>
      <CardMark />
      <ServicesTeaser />
      <TeamSection />
      <CustomersSection />
    </div>
  );
};

export default Home;
